import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { VideoCard, VideoGrid } from "@/components/videos/VideoCard";
import { useVideosData } from "@/hooks/useVideosData";
import { getHistory, HistoryItem, VideoItem } from "@/lib/videosApi";
import {
  Play,
  ArrowRight,
  Sparkles,
  Lightbulb,
  LayoutGrid,
  GraduationCap,
} from "lucide-react";

export const VideosHomePage: React.FC = () => {
  const { videos, categories, collections, loading, error, refetch } =
    useVideosData();
  const [activeFilter, setActiveFilter] = useState<string>("All");
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);

  useEffect(() => {
    const updateHistory = () => {
      setHistoryItems(getHistory());
    };
    updateHistory();
    window.addEventListener("jnn-videos-history-updated", updateHistory);
    return () =>
      window.removeEventListener("jnn-videos-history-updated", updateHistory);
  }, []);

  const totalVideos = videos.length;
  const totalCollections = collections.length;

  const filteredVideos = videos.filter((v) => {
    if (activeFilter === "All") return true;
    if (activeFilter === "Tips") return v.category?.toLowerCase() === "tips";
    if (activeFilter === "Before & After")
      return (
        v.category?.toLowerCase() === "before-after" ||
        v.category?.toLowerCase() === "before & after" ||
        v.title.toLowerCase().includes("before")
      );
    if (activeFilter === "Setups")
      return (
        v.category?.toLowerCase() === "setups" ||
        v.title.toLowerCase().includes("setup")
      );
    return true;
  });

  // Build VideoObject schemas
  const videoSchemas = videos.map((v) => ({
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: v.title,
    description: `Vacation rental management video: ${v.title}`,
    thumbnailUrl: v.thumbnail || `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`,
    uploadDate: v.date
      ? new Date(v.date).toISOString()
      : new Date().toISOString(),
    embedUrl: `https://www.youtube.com/embed/${v.id}`,
    publisher: {
      "@type": "Organization",
      name: "JNN Homes",
      logo: {
        "@type": "ImageObject",
        url: "https://vibe.filesafe.space/meta/1778679421893490393/og_image.png",
      },
    },
  }));

  const categoryIcons: Record<string, any> = {
    tips: Lightbulb,
    "before-after": Sparkles,
    setups: LayoutGrid,
  };

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title="Vacation Rental Management Videos | JNN Homes Northern Virginia"
        description="Short videos from JNN Homes on vacation rental management in Northern Virginia: hosting tips, before & after makeovers, and home setups for guests."
        canonical="https://www.jnnhomes.com/videos"
        schemaJson={videoSchemas.length > 0 ? videoSchemas : undefined}
      />

      <div className="space-y-10">
        {/* Header Section */}
        <div className="space-y-3 border-b border-slate-100 pb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            JNN Homes Videos
          </h1>
          <p className="text-sm sm:text-base text-slate-600">
            Vacation rental management tips, before & afters, and setups
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
            <p className="text-xs text-slate-500 font-medium">
              {totalVideos} videos · {totalCollections} collections · updates
              automatically from our YouTube channel
            </p>
            <a
              href="https://www.youtube.com/@JNNHomes?sub_confirmation=1"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] inline-flex items-center gap-2 shadow-xs"
            >
              Subscribe on YouTube
            </a>
          </div>
        </div>

        {/* Continue Watching Row (if history exists) */}
        {historyItems.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Play className="w-4 h-4 text-[#E8850F]" /> Continue Watching
              </h2>
              <Link
                to="/videos/history"
                className="text-xs font-semibold text-[#9A5207] hover:underline flex items-center gap-1"
              >
                View all history <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-none snap-x">
              {historyItems.slice(0, 6).map((item) => (
                <Link
                  key={item.id}
                  to={item.href}
                  className="shrink-0 w-40 sm:w-48 group snap-start"
                >
                  <div className="relative aspect-[9/16] rounded-xl overflow-hidden bg-slate-100 border border-slate-200 group-hover:border-[#E8850F] transition-colors">
                    <img
                      src={`https://i.ytimg.com/vi/${item.id}/hqdefault.jpg`}
                      alt={`Thumbnail for ${item.t}`}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-103 transition-transform"
                    />
                    <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center">
                      <div className="w-9 h-9 rounded-full bg-[#E8850F] text-white flex items-center justify-center shadow-md">
                        <Play className="w-4 h-4 fill-white ml-0.5" />
                      </div>
                    </div>
                  </div>
                  <p className="mt-2 text-xs font-semibold text-slate-900 line-clamp-2 group-hover:text-[#9A5207]">
                    {item.t}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Browse By Category Tiles */}
        <section className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">
            Browse by category
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((cat) => {
              const Icon = categoryIcons[cat.slug] || LayoutGrid;
              return (
                <Link
                  key={cat.slug}
                  to={`/videos/c/${cat.slug}`}
                  className="p-4 rounded-xl border border-slate-200 bg-white hover:border-[#E8850F] hover:bg-slate-50/50 transition-all group flex items-start gap-3 shadow-2xs"
                >
                  <div className="p-2.5 rounded-lg bg-[#E8850F]/10 text-[#9A5207] shrink-0 group-hover:bg-[#E8850F]/20 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#9A5207] transition-colors truncate">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {cat.comingSoon
                        ? "New videos soon"
                        : `${cat.count || 0} videos`}
                    </p>
                  </div>
                </Link>
              );
            })}
            {/* Academy Coming Soon tile */}
            <Link
              to="/videos/academy"
              className="p-4 rounded-xl border border-slate-200 bg-white hover:border-[#E8850F] hover:bg-slate-50/50 transition-all group flex items-start gap-3 shadow-2xs"
            >
              <div className="p-2.5 rounded-lg bg-[#E8850F]/10 text-[#9A5207] shrink-0">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#9A5207] transition-colors">
                    Academy
                  </h3>
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-100 text-slate-600">
                    Soon
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">Coming soon</p>
              </div>
            </Link>
          </div>
        </section>

        {/* Courses & Playlists */}
        {collections.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900">
                Courses & playlists
              </h2>
              <Link
                to="/videos/playlists"
                className="text-xs font-semibold text-[#9A5207] hover:underline flex items-center gap-1"
              >
                See all playlists <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {collections.map((col) => {
                const firstVideoId = col.lessons[0];
                return (
                  <Link
                    key={col.slug}
                    to={`/videos/course/${col.slug}`}
                    className="p-4 rounded-2xl border border-slate-200 bg-white hover:border-[#E8850F] transition-all group flex flex-col justify-between space-y-4 shadow-xs"
                  >
                    <div className="space-y-3">
                      {/* 3 small thumbnails preview */}
                      <div className="grid grid-cols-3 gap-1.5 rounded-xl overflow-hidden bg-slate-100 p-1">
                        {col.lessons.slice(0, 3).map((vId, idx) => (
                          <div
                            key={idx}
                            className="aspect-[9/16] bg-slate-200 rounded-lg overflow-hidden relative"
                          >
                            <img
                              src={`https://i.ytimg.com/vi/${vId}/hqdefault.jpg`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-[#E8850F]/14 text-[#9A5207]">
                            {col.category}
                          </span>
                          <span className="text-xs font-semibold text-slate-500">
                            {col.lessonCount} lessons
                          </span>
                        </div>
                        <h3 className="text-base font-bold text-slate-900 group-hover:text-[#9A5207] transition-colors leading-snug">
                          {col.title}
                        </h3>
                        <p className="text-xs text-slate-600 line-clamp-2 mt-1">
                          {col.description}
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-[#9A5207]">
                      <span>Start watching</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Latest Videos Grid with Filter Chips */}
        <section className="space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h2 className="text-lg font-bold text-slate-900">Latest videos</h2>
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              {["All", "Tips", "Before & After", "Setups"].map((chip) => (
                <button
                  key={chip}
                  onClick={() => setActiveFilter(chip)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#D97706] whitespace-nowrap ${
                    activeFilter === chip
                      ? "bg-[#B35A06] text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          <VideoGrid>
            {filteredVideos.map((video) => {
              // Find if this video belongs to a collection
              const parentCol = collections.find((c) =>
                c.lessons.includes(video.id),
              );
              return (
                <VideoCard
                  key={video.id}
                  video={video}
                  collectionSlug={parentCol?.slug}
                />
              );
            })}
          </VideoGrid>
        </section>
      </div>
    </VideosLayout>
  );
};
